import {
  AnalyticsPageFields,
  type ProductAnalyticsDemoQuery,
  type ProductAnalyticsPageQuery,
  ProductAnalyticsPageQuerySchema,
  type ProductAnalyticsQuery,
  ProductAnalyticsQuerySchema,
} from '@shared/schemas/productAnalytics';

export const PRODUCT_ANALYTICS_PATH = '/admin/product-analytics';
const fieldNames = Object.keys(AnalyticsPageFields) as Array<
  keyof typeof AnalyticsPageFields
>;

/** Field-level recovery for copied URLs; a bad field never clears a valid scope. */
export function parseProductAnalyticsSearch(search: string): {
  state: ProductAnalyticsPageQuery;
  notices: string[];
} {
  const params = new URLSearchParams(search);
  const defaults = ProductAnalyticsPageQuerySchema.parse({});
  const values: Record<string, unknown> = { ...defaults };
  const notices: string[] = [];
  for (const key of fieldNames) {
    if (!params.has(key)) continue;
    const parsed = AnalyticsPageFields[key].safeParse(params.get(key));
    if (parsed.success && params.getAll(key).length === 1)
      values[key] = parsed.data;
    else notices.push(`Reset invalid ${key}.`);
  }
  const unknown = [...new Set(params.keys())].filter(
    key => !fieldNames.includes(key as keyof typeof AnalyticsPageFields)
  );
  if (unknown.length) notices.push('Removed unsupported URL parameters.');
  let validated = ProductAnalyticsPageQuerySchema.safeParse(values);
  if (!validated.success) {
    // Date bounds are one dependent group. Preserve the audience and every
    // independent selector when the imported range cannot be used.
    for (const issue of validated.error.issues) {
      if (issue.path[0] === 'asOf') delete values.asOf;
      else if (issue.path[0] === 'from' || issue.path[0] === 'to') {
        values.preset = '28d';
        delete values.from;
        delete values.to;
      }
    }
    notices.push('Reset invalid reporting dates or observation time.');
    validated = ProductAnalyticsPageQuerySchema.safeParse(values);
  }
  const state = validated.success ? validated.data : defaults;
  if (
    state.tab === 'demo' &&
    (state.detail === 'users' || state.detail === 'uptake')
  ) {
    delete state.detail;
    delete state.metric;
    state.page = 1;
    notices.push('Closed a logged-in metric detail on the Public demo tab.');
  }
  if (
    state.detail === 'users' &&
    (!state.metric ||
      ((state.metric === 'original_cohort' ||
        state.metric === 'returning_cohort') &&
        !state.cohortStart) ||
      (state.metric === 'returning_cohort' && !state.week))
  ) {
    delete state.detail;
    delete state.metric;
    state.page = 1;
    notices.push('Closed an incomplete metric detail.');
  }
  if (state.detail === 'history' && !state.userId) {
    state.detail = 'exclusions';
    state.page = 1;
    notices.push(
      'Returned to analytics exclusions because no valid account was selected.'
    );
  }
  return { state, notices };
}

export function productAnalyticsHref(state: ProductAnalyticsPageQuery): string {
  const parsed = ProductAnalyticsPageQuerySchema.parse(state);
  const defaults = ProductAnalyticsPageQuerySchema.parse({});
  const params = new URLSearchParams();
  for (const key of fieldNames) {
    const value = parsed[key];
    if (value !== undefined && value !== defaults[key])
      params.set(key, String(value));
  }
  return `${PRODUCT_ANALYTICS_PATH}${params.size ? `?${params}` : ''}`;
}

/** Narrow profile-origin allowlist; no hooks or router dependencies. */
export function validatedProductAnalyticsReturnTo(
  value: unknown
): string | null {
  if (
    typeof value !== 'string' ||
    !value ||
    /[\\#\r\n]/.test(value) ||
    !/^\/admin\/product-analytics(?:\?|$)/.test(value)
  )
    return null;
  const query = value.slice(PRODUCT_ANALYTICS_PATH.length);
  const params = new URLSearchParams(query);
  if (params.has('returnTo')) return null;
  const parsed = parseProductAnalyticsSearch(query);
  if (parsed.notices.length) return null;
  return productAnalyticsHref(parsed.state);
}

export function productAnalyticsReportQuery(
  state: ProductAnalyticsPageQuery
): ProductAnalyticsQuery {
  const { preset, from, to, asOf, k, population, organizationId, cohortId } =
    state;
  return ProductAnalyticsQuerySchema.parse({
    preset,
    from,
    to,
    asOf,
    k,
    population,
    organizationId,
    cohortId,
  });
}

export function productAnalyticsDemoQuery(
  state: ProductAnalyticsPageQuery
): ProductAnalyticsDemoQuery {
  const { preset, from, to, asOf } = state;
  return { preset, from, to, asOf };
}

export function productAnalyticsReportState(
  state: ProductAnalyticsPageQuery
): ProductAnalyticsPageQuery {
  const next = { ...state, page: 1 };
  delete next.detail;
  delete next.metric;
  delete next.userId;
  delete next.cohortStart;
  delete next.week;
  return next;
}
